<template>
  <div class="portion-stepper" :class="{ 'is-range': isRange }">
    <span class="portion-label">{{ label }}</span>

    <!-- Range mode: read-only display, no controls. -->
    <div v-if="isRange" class="portion-range" :title="'Recipe specifies a range'">
      <span class="portion-range-value">{{ rangeLabel }}</span>
    </div>

    <!-- Normal mode: full stepper. Reset is always rendered so width stays
         stable - it just becomes invisible when the value is unchanged.
         Reset is placed BEFORE the controls so it appears to the left of the
         +/- stepper. -->
    <template v-else>
      <button
        type="button"
        class="reset-btn"
        :class="{ shown: value !== original }"
        :aria-hidden="value === original"
        :tabindex="value === original ? -1 : 0"
        :title="'Nulstil til ' + original"
        @click="reset"
      >
        <fa icon="undo" />
      </button>
      <div class="portion-controls">
        <button
          type="button"
          class="step-btn"
          :disabled="value <= min"
          :aria-label="'Færre ' + label.toLowerCase()"
          @click="decrease"
        >−</button>
        <span class="portion-count" :class="{ modified: value !== original }">{{ value }}</span>
        <button
          type="button"
          class="step-btn"
          :disabled="value >= max"
          :aria-label="'Flere ' + label.toLowerCase()"
          @click="increase"
        >+</button>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'Portions',
  props: {
    value:      { type: Number, required: true },
    original:   { type: Number, required: true },
    label:      { type: String, default: 'Portioner' },
    rangeLabel: { type: String, default: '' },
    min:        { type: Number, default: 1 },
    max:        { type: Number, default: 50 }
  },
  computed: {
    isRange() {
      return !!this.rangeLabel
    }
  },
  methods: {
    increase() {
      if (this.value < this.max) this.$emit('input', this.value + 1)
    },
    decrease() {
      if (this.value > this.min) this.$emit('input', this.value - 1)
    },
    reset() {
      this.$emit('input', this.original)
    }
  }
}
</script>

<style lang="scss" scoped>
.portion-stepper {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  // Always sit to the right edge of the ingredients controls bar - if the
  // unit toggle is present it sits to the left, otherwise the stepper alone
  // gets pushed to the right by this auto margin.
  margin-left: auto;
}

.portion-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--dark);
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.portion-controls {
  display: inline-flex;
  align-items: stretch;
  background: white;
  border-radius: 999px;
  box-shadow: var(--cardshadow);
  overflow: hidden;
  user-select: none;
}

.step-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: white;
  color: var(--dark);
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.05s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: var(--main);
    color: white;
  }
  &:active:not(:disabled) { transform: scale(0.94); }
  &:focus-visible {
    outline: 2px solid var(--darker);
    outline-offset: -2px;
  }
  &:disabled {
    color: #c5c5c5;
    cursor: not-allowed;
  }
}

.portion-count {
  min-width: 32px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--dark);
  background: white;
  font-variant-numeric: tabular-nums;
  border-left: 1px solid rgba(0, 0, 0, 0.06);
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  transition: color 0.15s ease;

  &.modified { color: var(--darker); }
}

// Reset is rendered in all stepper states - it occupies space so showing /
// hiding it doesn't shift surrounding layout. We fade visibility/interaction
// rather than mounting/unmounting.
.reset-btn {
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--dark);
  cursor: pointer;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.18s ease, background 0.15s ease, color 0.15s ease;

  &.shown {
    opacity: 0.65;
    pointer-events: auto;
  }
  &.shown:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.05);
    color: var(--darker);
  }
}

// Range mode - read-only display in the same pill style as the stepper.
.portion-range {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 14px;
  background: white;
  border-radius: 999px;
  box-shadow: var(--cardshadow);
  cursor: default;
}

.portion-range-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--dark);
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}
</style>
